import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, CurrencyDollar, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIER_ICON_CLASS = "h-6 w-6";

export default function Subscriptions() {
  const { hid } = useHousehold();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!hid) return;
    try {
      const r = await api.get(`/subscriptions?household_id=${hid}`);
      setState(r.data);
    } catch (e) {
      toast.error("Could not load subscriptions");
    }
  };

  useEffect(() => { load(); }, [hid]);

  const toggle = async (tier, currentlyActive) => {
    setBusy(tier);
    try {
      const endpoint = currentlyActive ? "unsubscribe" : "subscribe";
      await api.post(`/subscriptions/${endpoint}?household_id=${hid}`, { tier });
      toast.success(currentlyActive ? `Unsubscribed from ${tier}` : `Subscribed to ${tier}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(null);
    }
  };

  if (!state) return null;

  const monthly = (state.catalog || [])
    .filter((t) => state.tiers?.[t.id]?.active)
    .reduce((s, t) => s + (t.price_monthly || 0), 0);

  return (
    <div data-testid="subscriptions-page" className="space-y-8 max-w-4xl">
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
          Subscriptions
        </p>
        <h1 className="font-serif text-3xl font-medium tracking-tight mt-1">
          À la carte modules
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl leading-relaxed">
          Net Worth and Universal Ingest are free forever. Subscribe only to the
          asset modules you actually use. Cancel anytime — your data stays.
        </p>
      </header>

      <div
        data-testid="subscriptions-total-card"
        className="rounded-lg border border-border bg-card p-5 flex items-center justify-between"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
            Monthly total
          </p>
          <p className="font-serif text-3xl font-medium mt-1">
            ${monthly.toLocaleString()}<span className="text-base text-muted-foreground">/mo</span>
          </p>
        </div>
        <CurrencyDollar size={32} weight="duotone" className="text-[hsl(18_53%_49%)]" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {(state.catalog || []).map((tier) => {
          const active = !!state.tiers?.[tier.id]?.active;
          return (
            <div
              key={tier.id}
              data-testid={`subscription-tier-${tier.id}`}
              className={`rounded-lg border-2 p-5 transition-all ${
                active
                  ? "border-[hsl(18_53%_49%)] bg-[hsl(18_53%_98%)]"
                  : "border-border bg-card"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                    Module
                  </p>
                  <h3 className="font-serif text-xl leading-tight mt-0.5">{tier.name}</h3>
                </div>
                {active && (
                  <span
                    data-testid={`subscription-active-badge-${tier.id}`}
                    className="text-[10px] uppercase tracking-wider font-bold text-[hsl(18_53%_49%)] flex items-center gap-1"
                  >
                    <CheckCircle size={14} weight="fill" /> Active
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 min-h-[40px]">
                {tier.description}
              </p>
              <div className="flex items-baseline justify-between mb-4">
                <p className="font-serif text-2xl font-medium">
                  ${tier.price_monthly}<span className="text-sm text-muted-foreground">/mo</span>
                </p>
                {tier.asset_types?.length > 0 && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {tier.asset_types.join(" · ").replace(/_/g, " ")}
                  </p>
                )}
              </div>
              <Button
                data-testid={`subscription-toggle-${tier.id}`}
                onClick={() => toggle(tier.id, active)}
                disabled={busy === tier.id}
                variant={active ? "outline" : "default"}
                className={`w-full rounded-sm ${active ? "" : "bg-[hsl(18_53%_49%)] hover:bg-[hsl(18_53%_42%)] text-white"}`}
              >
                {busy === tier.id ? "Updating…" : active ? "Unsubscribe" : "Subscribe"}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-dashed border-border p-4 flex items-start gap-3">
        <Lightning size={18} weight="duotone" className="text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Billing is currently in preview mode — toggles are immediate, no payment is taken yet.
          Stripe Checkout will be wired in the next release.
        </p>
      </div>
    </div>
  );
}
